// src/pages/Profile.js
import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import API from "../api";
import { FaUser, FaCog } from "react-icons/fa";
import LoadingSpinner from "../components/LoadingSpinner";
import StatCard from "../components/StatCard";
import PageHeader from "../components/ui/PageHeader";
import PageShell from "../components/ui/PageShell";
import Sparkline from "../components/ui/Sparkline";
import ChartNumbers from "../components/ui/ChartNumbers";
import SegmentedTabs from "../components/SegmentedTabs";
import { usePageTitle } from "../hooks/usePageTitle";
import { motion } from "framer-motion";
import { UserContext } from "../context/UserContext";
import { SHOW_GROUPS } from "../constants/featureFlags";
import { COMPETITION_OPTIONS } from "../constants/competitionPrefs";
import {
  CURRENT_SEASON,
  SEASON_OPTIONS,
  formatSeasonLabel,
} from "../constants/season";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const asList = (value) => (Array.isArray(value) ? value : []);
const competitionName = (id) => COMPETITION_OPTIONS.find((c) => c.id === id)?.label || id;

export default function Profile() {
  usePageTitle('Profile');
  
  // ─── State ───────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [plScores, setPlScores] = useState([]);
  const [uclScores, setUclScores] = useState([]);
  const [wildcardScores, setWildcardScores] = useState([]);
  const [tableScore, setTableScore] = useState(null);
  const [tableRank, setTableRank] = useState(null);
  const [globalRank, setGlobalRank] = useState(null);
  const [laligaScores, setLaligaScores] = useState([]);
  const [bundesligaScores, setBundesligaScores] = useState([]);
  const [serieAScores, setSerieAScores] = useState([]);
  const [error, setError] = useState("");
  const [showSeasonRecap, setShowSeasonRecap] = useState(false);
  const [seasonRecap, setSeasonRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const [recapError, setRecapError] = useState("");
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const { user } = useContext(UserContext);

  const loadSeasonData = async (season) => {
    try {
      const { data } = await API.get("/profile/gameweeks", {
        params: { competition: "premier_league", season },
      });
      setPlScores(data);
    } catch (e) {
      console.error(e);
      setPlScores([]);
    }
    try {
      const { data } = await API.get("/profile/ucl-scores", { params: { season } });
      setUclScores(data);
    } catch (e) {
      console.error(e);
      setUclScores([]);
    }
    try {
      const { data } = await API.get("/profile/gameweeks", {
        params: { competition: "wildcard", season },
      });
      setWildcardScores(data);
    } catch (e) {
      console.error(e);
      setWildcardScores([]);
    }
    for (const [competition, setter] of [
      ["laliga", setLaligaScores],
      ["bundesliga", setBundesligaScores],
      ["serie_a", setSerieAScores],
    ]) {
      try {
        const { data } = await API.get("/profile/gameweeks", {
          params: { competition, season },
        });
        setter(data || []);
      } catch (e) {
        console.error(e);
        setter([]);
      }
    }
  };

  // ─── Load data on mount ───────────────────────────────────
  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    loadSeasonData(selectedSeason);
    setSeasonRecap(null);
    fetchTableStats(selectedSeason);
  }, [selectedSeason]);

  useEffect(() => {
    if (!user?.username) {
      setGlobalRank(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/leaderboard", {
          params: { competition: "premier_league", season: selectedSeason },
        });
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        const userEntry = rows.find((entry) => entry.username === user.username);
        setGlobalRank(userEntry ? userEntry.rank : null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setGlobalRank(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.username, selectedSeason]);

  const fetchProfile = async () => {
    try {
      setError("");
      const { data } = await API.get("/profile");
      setProfile(data);
    } catch (e) {
      console.error(e);
      setError("Failed to load profile data.");
    }
  };

  const fetchTableStats = async (season = CURRENT_SEASON) => {
    try {
      const { data } = await API.get("/table-predictions/my-table-score", {
        params: { season },
      });
      setTableScore(data.total_table_points ?? 0);
      setTableRank(data.rank ?? "-");
    } catch (e) {
      console.error(e);
    }
  };

  // ─── Derived season metrics ────────────────────────────────
  const allRounds = [
    ...asList(plScores).map((r) => ({ ...r, competition: "premier_league" })),
    ...asList(laligaScores).map((r) => ({ ...r, competition: "laliga" })),
    ...asList(bundesligaScores).map((r) => ({ ...r, competition: "bundesliga" })),
    ...asList(serieAScores).map((r) => ({ ...r, competition: "serie_a" })),
    ...asList(wildcardScores).map((r) => ({ ...r, competition: "wildcard" })),
    ...asList(uclScores).map((r) => ({ ...r, competition: "ucl" })),
  ];

  const byCompetition = Object.values(
    allRounds.reduce((acc, round) => {
      const id = round.competition;
      const current = acc[id] || { id, points: 0, exact: 0, matches: 0, weeks: [] };
      current.points += Number(round.total_points) || 0;
      current.exact += Number(round.perfect_predictions) || 0;
      current.matches += Number(round.games_predicted) || 0;
      current.weeks.push(round);
      acc[id] = current;
      return acc;
    }, {})
  )
    .filter((row) => row.matches > 0 || row.points > 0)
    .sort((a, b) => b.points - a.points);

  const chartLeague = byCompetition[0];
  const chartWeeks = (chartLeague?.weeks || [])
    .filter((w) => (Number(w.games_predicted) || 0) > 0 || (Number(w.total_points) || 0) > 0)
    .slice()
    .sort((a, b) => Number(a.week_number ?? 0) - Number(b.week_number ?? 0));

  const totalSeasonPoints = allRounds.reduce(
    (sum, r) => sum + (Number(r.total_points) || 0),
    0
  );

  const totalGamesPredicted = allRounds.reduce(
    (sum, r) => sum + (Number(r.games_predicted) || 0),
    0
  );

  const openSeasonRecap = async () => {
    setShowSeasonRecap(true);
    setRecapLoading(true);
    setRecapError("");
    try {
      const { data } = await API.get(
        `/season/${encodeURIComponent(selectedSeason)}/recap`
      );
      setSeasonRecap(data);
    } catch (e) {
      console.error("Error loading season recap:", e);
      setRecapError(
        e?.response?.data?.message ||
          "Failed to load season recap. Please try again."
      );
    } finally {
      setRecapLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen profile-bg flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm text-center">
          <p className="text-red-600 text-sm font-medium mb-4">{error}</p>
          <button
            type="button"
            onClick={() => {
              setError("");
              fetchProfile();
            }}
            className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!profile)
    return <LoadingSpinner message="Loading profile..." />;

  return (
    <div className="min-h-screen bg-paper">
      <PageShell
        stickyRail
        rail={
          byCompetition.length > 0 ? (
            <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
              <h2 className="px-4 py-3 border-b border-gray-100 text-[15px] font-semibold text-ink">By competition</h2>
              <ul className="divide-y divide-line">
                {byCompetition.map((row) => (
                  <li key={row.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-ink truncate">{competitionName(row.id)}</p>
                      <p className="flex items-center gap-2 text-[17px] font-bold tabular-nums text-ink">
                        <Sparkline
                          values={[...row.weeks]
                            .sort((a, b) => Number(a.week_number ?? 0) - Number(b.week_number ?? 0))
                            .map((week) => Number(week.total_points) || 0)}
                        />
                        {row.points}
                      </p>
                    </div>
                    <p className="text-[12px] text-muted">
                      {row.matches} picked · {row.exact} exact
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null
        }
      >
        <div className="space-y-4">
        <PageHeader
          title="You"
          meta={globalRank != null ? `Rank #${globalRank} in the Premier League` : "Prediction League member"}
          actions={
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-line bg-white text-sm font-semibold text-gray-700 hover:border-gray-400"
            >
              <FaCog className="w-4 h-4" aria-hidden />
              Settings
            </Link>
          }
        />

        {SEASON_OPTIONS.length > 1 && (
          <SegmentedTabs
            items={SEASON_OPTIONS.map((s) => ({ key: s.id, label: s.label }))}
            activeKey={selectedSeason}
            onChange={setSelectedSeason}
          />
        )}

        <section className="rounded-xl border border-line bg-white shadow-sm p-4 md:p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
              <FaUser className="text-lg" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-[17px] font-semibold text-ink truncate">{profile.username}</h2>
              <p className="text-meta text-muted">{formatSeasonLabel(selectedSeason)} season</p>
            </div>
          </div>
          <button
            type="button"
            onClick={openSeasonRecap}
            className="shrink-0 h-10 px-3 md:px-4 rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-semibold"
          >
            <span className="md:hidden">Recap</span>
            <span className="hidden md:inline">Season recap</span>
          </button>
        </section>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard title="Season points" value={totalSeasonPoints} subtitle={formatSeasonLabel(selectedSeason)} />
          <StatCard title="All-time points" value={profile.total_points || 0} subtitle="All competitions" />
          <StatCard
            title="Exact scores"
            value={allRounds.reduce((sum, s) => sum + (Number(s.perfect_predictions) || 0), 0)}
            subtitle="5-point picks"
          />
          <StatCard title="Matches picked" value={totalGamesPredicted} />
          {tableScore !== null && (
            <>
              <StatCard title="Table score" value={tableScore} subtitle="Final-table prediction" />
              <StatCard title="Table rank" value={tableRank ? `#${tableRank}` : "—"} />
            </>
          )}
        </div>

        {chartWeeks.length > 0 && (
          <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
            <div className="px-4 md:px-5 py-3 border-b border-gray-100">
              <h2 className="text-[15px] font-semibold text-ink">Points by week</h2>
              <p className="text-[12px] text-muted">{competitionName(chartLeague.id)}</p>
            </div>
            <div className="h-52 md:h-64 p-4">
              <Line
                data={{
                  labels: chartWeeks.map((w, i) =>
                    w.week_number != null ? `W${w.week_number}` : w.stage_name || `${i + 1}`
                  ),
                  datasets: [
                    {
                      data: chartWeeks.map((w) => Number(w.total_points) || 0),
                      borderColor: "#1e3a8a",
                      backgroundColor: "#1e3a8a",
                      tension: 0.3,
                      pointRadius: 2,
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false }, ticks: { maxTicksLimit: 8, font: { size: 11 } } },
                    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#f3f4f6" } },
                  },
                }}
                aria-label={`Points by week, ${competitionName(chartLeague.id)}`}
              />
            </div>
            <ChartNumbers
              caption={`Points by week, ${competitionName(chartLeague.id)}`}
              columns={["Week", "Points"]}
              rows={chartWeeks.map((week, index) => [
                week.week_number != null ? `W${week.week_number}` : week.stage_name || `${index + 1}`,
                Number(week.total_points) || 0,
              ])}
            />
          </section>
        )}
        </div>
      </PageShell>
      {/* Season Recap Modal */}
      {showSeasonRecap && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowSeasonRecap(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                Season {formatSeasonLabel(selectedSeason)} Recap
              </h2>
              <motion.button
                type="button"
                onClick={() => setShowSeasonRecap(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="text-gray-500 hover:text-gray-900 text-sm font-medium"
              >
                Close
              </motion.button>
            </div>

            {recapLoading && (
              <LoadingSpinner message="Loading season recap…" fullscreen={false} />
            )}

            {!recapLoading && recapError && (
              <p className="text-center text-red-600 py-6 text-sm">
                {recapError}
              </p>
            )}

            {!recapLoading && !recapError && seasonRecap && (
              <div className="space-y-6">
                {/* Champion & Your Best Round */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      Season Champion (Premier League)
                    </h3>
                    {seasonRecap.champion ? (
                      <>
                        <p className="text-xl font-extrabold text-gray-900">
                          {seasonRecap.champion.username}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {seasonRecap.champion.total_points} pts across the
                          season
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-600">
                        No champion data yet.
                      </p>
                    )}
                  </div>

                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      Your Best Round
                    </h3>
                    {seasonRecap.user_best_round ? (
                      <>
                        <p className="text-xl font-extrabold text-gray-900">
                          {seasonRecap.user_best_round.total_points} pts
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {seasonRecap.user_best_round.label}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-600">
                        Make predictions this season to unlock your best round.
                      </p>
                    )}
                  </div>
                </div>

                {/* Top 10 Users */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Top 10 (Premier League)
                  </h3>
                  {seasonRecap.top_users?.length ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs md:text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 text-gray-500">
                            <th className="py-2 pr-3 text-left">Rank</th>
                            <th className="py-2 pr-3 text-left">User</th>
                            <th className="py-2 text-right">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seasonRecap.top_users.map((u, idx) => (
                            <tr
                              key={u.id}
                              className="border-b last:border-b-0 border-gray-100"
                            >
                              <td className="py-2 pr-3 font-mono">#{idx + 1}</td>
                              <td className="py-2 pr-3">{u.username}</td>
                              <td className="py-2 text-right font-semibold">
                                {u.total_points}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600">
                      No leaderboard data yet for this season.
                    </p>
                  )}
                </div>

                {/* Top Groups */}
                {SHOW_GROUPS && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Top Groups (Premier League)
                  </h3>
                  {seasonRecap.top_groups?.length ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs md:text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 text-gray-500">
                            <th className="py-2 pr-3 text-left">Group</th>
                            <th className="py-2 pr-3 text-left">Members</th>
                            <th className="py-2 pr-3 text-right">
                              Total Points
                            </th>
                            <th className="py-2 text-right">Avg / Member</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seasonRecap.top_groups.map((g) => (
                            <tr
                              key={g.id}
                              className="border-b last:border-b-0 border-gray-100"
                            >
                              <td className="py-2 pr-3">{g.name}</td>
                              <td className="py-2 pr-3">{g.member_count}</td>
                              <td className="py-2 pr-3 text-right font-semibold">
                                {g.total_points}
                              </td>
                              <td className="py-2 text-right font-semibold">
                                {Number(g.avg_points).toFixed(1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600">
                      No group data yet for this season.
                    </p>
                  )}
                </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
